import { ChangeDetectionStrategy, Component } from '@angular/core';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Router, RouterLink } from '@angular/router';
import { connectState } from '@examdojo/angular/util';
import { SyllabusQuery, SyllabusUIModel } from '@examdojo/category';
import { Icon, IconComponent } from '@examdojo/core/icon';
import {
  DerivativeQuestionReviewType,
  QUESTION_REVIEW_TYPE_TO_REVIEW_PERMISSION,
  REFERENCE_QUESTION_REVIEW_TYPE_TO_REVIEW_PERMISSION,
  ReferenceQuestionReviewType,
  ReviewPermission,
  ReviewPermissionsQuery,
} from '@examdojo/review';
import { ExamdojoSupabaseService } from '@examdojo/supabase';
import { combineLatest, from, map } from 'rxjs';
import { RootUrlParts, SyllabusUrlParts } from '../app.model';
import { getAllQuestionReviewConfigs } from '../validation/derivative-question-validation/derivative-question-validation.model';
import { getAllReferenceQuestionReviewConfigs } from '../validation/ref-question-validation/ref-question-validation.model';

export interface ReviewWorkCount {
  type: ReferenceQuestionReviewType | DerivativeQuestionReviewType;
  count: number;
  displayName: string;
  icon: Icon;
}

export interface SyllabusReviewCounts {
  referenceQuestions: ReviewWorkCount[];
  derivativeQuestions: ReviewWorkCount[];
}

interface ContributorOpenTasksBySyllabus {
  user_id: string;
  syllabus_id: number;
  open_tasks_by_permission: Record<ReviewPermission, number>;
}

type ContributorOpenTasksResponse = ContributorOpenTasksBySyllabus[];

@Component({
  selector: 'dojo-validation-overview',
  imports: [RouterLink, IconComponent, MatTooltipModule],
  templateUrl: './overview.component.html',
  styleUrl: './overview.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class OverviewComponent {
  constructor(
    private readonly syllabusQuery: SyllabusQuery,
    private readonly reviewPermissionsQuery: ReviewPermissionsQuery,
    private readonly router: Router,
    private readonly supabase: ExamdojoSupabaseService,
  ) {}

  readonly state = connectState({
    accessibleSyllabuses: combineLatest([
      this.syllabusQuery.selectAllEntities(),
      this.reviewPermissionsQuery.selectAllowedSyllabuses(),
    ]).pipe(
      map(([allSyllabuses, allowedSyllabusIds]) =>
        allSyllabuses.filter((syllabus) => allowedSyllabusIds.includes(syllabus.id)),
      ),
    ),
    reviewDataBySyllabus: from(this.fetchContributorOpenTasks()).pipe(
      map((data) => (data && data.length > 0 ? this.transformDataToReviewCountsBySyllabus(data) : new Map())),
    ),
  });

  private readonly expandedSyllabuses = new Set<number>();

  getSyllabusRoute(syllabus: SyllabusUIModel): string[] {
    return ['/', RootUrlParts.Syllabus, syllabus.slug, SyllabusUrlParts.Validate];
  }

  getReviewCounts(syllabusId: number): SyllabusReviewCounts {
    const defaultCounts: SyllabusReviewCounts = {
      referenceQuestions: [],
      derivativeQuestions: [],
    };

    if (!this.state.reviewDataBySyllabus) {
      return defaultCounts;
    }

    return this.state.reviewDataBySyllabus.get(syllabusId) ?? defaultCounts;
  }

  isExpanded(syllabusId: number): boolean {
    return this.expandedSyllabuses.has(syllabusId);
  }

  toggleExpanded(event: Event, syllabusId: number): void {
    event.preventDefault();
    event.stopPropagation();
    if (this.expandedSyllabuses.has(syllabusId)) {
      this.expandedSyllabuses.delete(syllabusId);
    } else {
      this.expandedSyllabuses.add(syllabusId);
    }
  }

  getGrandTotal(counts: SyllabusReviewCounts): number {
    return this.getTotalCount(counts.referenceQuestions) + this.getTotalCount(counts.derivativeQuestions);
  }

  navigateToReview(
    event: Event,
    syllabus: SyllabusUIModel,
    reviewType: ReferenceQuestionReviewType | DerivativeQuestionReviewType,
    isReference: boolean,
  ): void {
    event.preventDefault();
    event.stopPropagation();

    const baseRoute = ['/', RootUrlParts.Syllabus, syllabus.slug, SyllabusUrlParts.Validate];
    const typeRoute = isReference ? 'reference-question' : 'derivative-question';
    const fullRoute = [...baseRoute, typeRoute, reviewType];

    this.router.navigate(fullRoute);
  }

  getTotalCount(counts: ReviewWorkCount[]): number {
    return counts.reduce((sum, item) => sum + item.count, 0);
  }

  private async fetchContributorOpenTasks(): Promise<ContributorOpenTasksResponse | null> {
    const { data, error } = await (
      this.supabase.client.schema('analytics' as unknown as 'public') as unknown as {
        rpc: (name: string) => Promise<{ data: ContributorOpenTasksResponse | null; error: unknown }>;
      }
    ).rpc('bi__contributor_open_tasks');

    if (error) {
      console.error('Error fetching contributor open tasks:', error);
      return null;
    }

    return data;
  }

  private transformDataToReviewCountsBySyllabus(data: ContributorOpenTasksResponse): Map<number, SyllabusReviewCounts> {
    const result = new Map<number, SyllabusReviewCounts>();

    for (const item of data) {
      const counts = this.transformTasksToReviewCounts(item.open_tasks_by_permission);
      result.set(item.syllabus_id, counts);
    }

    return result;
  }

  private transformTasksToReviewCounts(tasks: Record<ReviewPermission, number>): SyllabusReviewCounts {
    const refConfigs = getAllReferenceQuestionReviewConfigs();
    const derConfigs = getAllQuestionReviewConfigs();

    return {
      referenceQuestions: refConfigs.map((config) => ({
        type: config.urlPart,
        count: this.getCountForReferenceType(tasks, config.urlPart),
        displayName: config.displayName,
        icon: config.icon as Icon,
      })),
      derivativeQuestions: derConfigs.map((config) => ({
        type: config.urlPart,
        count: this.getCountForDerivativeType(tasks, config.urlPart),
        displayName: config.displayName,
        icon: config.icon,
      })),
    };
  }

  private getCountForReferenceType(data: Record<string, number>, type: ReferenceQuestionReviewType): number {
    return data[REFERENCE_QUESTION_REVIEW_TYPE_TO_REVIEW_PERMISSION[type]] ?? 0;
  }

  private getCountForDerivativeType(data: Record<string, number>, type: DerivativeQuestionReviewType): number {
    return data[QUESTION_REVIEW_TYPE_TO_REVIEW_PERMISSION[type]] ?? 0;
  }
}
